import React, { memo, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { LatLngBounds, latLngBounds, LatLngTuple } from 'leaflet';
import proj4 from 'proj4-fully-loaded';
import reprojectGeoJSON from 'reproject-geojson';
import { InstallationResult, PanelParameters, SurfaceCollection, ExistingPvResult } from '../types/types';
import { getPanelPlacement, getSurfaces, getExistingPv } from '../services/api';
import '../index.css'
import 'leaflet/dist/leaflet.css';
import MapLayout from '@syte-tech/ui/components/layout/MapLayout';
import MapSidebar from '../components/MapSidebar';
import MapSidebarHeader from '../components/MapSidebarHeader';
import Box from '@mui/material/Box';
import { LoadingOverlay } from '@syte-tech/ui/components/loading/LoadingSpinner';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import LoadingEllipsis from '@syte-tech/ui/components/loading/LoadingEllipsis';
import ResultMap from '../components/ResultMap';
import ParagraphLineWith from '@syte-tech/ui/components/common/ParagraphLineWith';
import Tabs, { TabContent } from '@syte-tech/ui/components/common/Tabs';
import Fab from '@mui/material/Fab';
import ArrowLeft from '@syte-tech/ui/icons/ArrowLeft';
import BarChartComponent from '../components/BarChartComponent';
import { Button } from '@mui/material';
import PanelParameterControls from '../components/PanelParameterControls';
import posthog from 'posthog-js';

// proj4-full-loaded already includes the definitions for EPSG:25832 and EPSG:25833
//proj4.defs('25832', '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs')
//proj4.defs('25833', '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs')

const MapLoadingOverlay = styled(LoadingOverlay)(({ theme }) => ({
  backgroundColor: theme.palette.grey[400],
}));

const AnnualPower = memo<{ series: number[] }>(({ series }) => (
  <>{
    Math.round(
      series.reduce((partialSum, val) => partialSum + val, 0)
      / 1000
    ).toLocaleString()
  } kWh/a</>
), (a, b) => a.series === b.series);

function sumSeries(power_series: Record<string, number[]>): number[] {
  // Sum the power series for each surface
  const arrays = Object.values(power_series);
  return arrays[0].map((_, i) =>
    arrays.reduce((sum, arr) => sum + arr[i], 0)
  );
}

const azimuthToCardinal = (azimuth: number, tilt?: number): string => {
  if ((tilt !== undefined) && tilt <= 5) {
    // If tilt is small, treat as flat roof
    return 'Flachdach';
  }
  if (azimuth >= 337.5 || azimuth < 22.5)
    return 'N';
  if (azimuth >= 22.5 && azimuth < 67.5)
    return 'NE';
  if (azimuth >= 67.5 && azimuth < 112.5)
    return 'E';
  if (azimuth >= 112.5 && azimuth < 157.5)
    return 'SE';
  if (azimuth >= 157.5 && azimuth < 202.5)
    return 'S';
  if (azimuth >= 202.5 && azimuth < 247.5)
    return 'SW';
  if (azimuth >= 247.5 && azimuth < 292.5)
    return 'W';
  if (azimuth >= 292.5 && azimuth < 337.5)
    return 'NW';
  return 'Unknown';
};

const ResultsPage: React.FC = () => {
  const { buildingId } = useParams<{ buildingId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState: { parameters?: PanelParameters } | undefined = location.state;

  const [installationResult, setInstallationResult] = useState<InstallationResult | null>(null);
  const [existingPvResult, setExistingPvResult] = useState<ExistingPvResult | null>(null);
  const [surfaces, setSurfaces] = useState<SurfaceCollection | null>(null);
  const [hoveredSurface, setHoveredSurface] = useState<string | null>(null);
  const [selectedSurfaces, setSelectedSurfaces] = useState<string[]>([]);
  const [mapBounds, setMapBounds] = useState<LatLngBounds | undefined>();
  const [newParameters, setNewParameters] = useState<PanelParameters | undefined>(locationState?.parameters)

  const [tabIndex, setTabIndex] = useState(0);

  const totalPanels = installationResult?.total_panel_count;
  const totalArea = installationResult?.total_panel_area;

  const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY;

  posthog.init(POSTHOG_API_KEY, { 
      api_host: 'https://t.syte.ms/', 
      defaults: '2025-05-24', 
      autocapture: false, 
  })

  const panelGeoJSON: GeoJSON.FeatureCollection | null = useMemo(() => {

    return installationResult && {
      type: 'FeatureCollection',
      features: installationResult.panels.map((panel, index) => (reprojectGeoJSON({
        type: 'Feature',
        geometry: panel.geometry,
        properties: {
          id: `panel-${index}`,
          type: 'panel'
        }
      }, { to: 4326, from: installationResult.crs })))
    };

  }, [installationResult]);

  const obstructionGeoJSON: GeoJSON.FeatureCollection | null = useMemo(() => {

    return installationResult && {
      type: 'FeatureCollection',
      features: installationResult.obstructions.map((obstruction, index) => (reprojectGeoJSON({
        type: 'Feature',
        geometry: obstruction.geometry,
        properties: {
          id: `obstruction-${index}`,
          type: 'obstruction'
        }
      }, { to: 4326, from: installationResult.crs })))
    };

  }, [installationResult]);

  const surfacesGeoJSON: GeoJSON.FeatureCollection | null = surfaces && {
    type: "FeatureCollection",
    features: surfaces.features.map((surface, index) => (reprojectGeoJSON({
      type: "Feature",
      geometry: surface.geometry,
      properties: {
        id: index,
        surface_gmlid: surface?.properties?.surface_gmlid,
        area: surface?.properties?.area_3d,
        azimuth: surface?.properties?.azimuth,
        tilt: surface?.properties?.tilt,
      }
    }, { to: 4326, from: surfaces.crs })))
  };

  const existingPvGeoJSON: GeoJSON.FeatureCollection | null = useMemo(() => {
    return existingPvResult && {
      type: 'FeatureCollection',
      features: existingPvResult.geometries.map((existing_pv, index) => (reprojectGeoJSON({
        type: 'Feature',
        geometry: existing_pv.geometry,
        properties: {
          ...existing_pv.properties,
          // id: `existing_pv-${index}`,
          type: 'existing_pv'
        }
      }, { to: 4326, from: existingPvResult.crs })))
    };

  }, [existingPvResult]);

  const totalAnnualPower = useMemo(() => {

    if (!installationResult) return 0;

    return Math.round(
      sumSeries(installationResult.pv_power_series)
        .reduce((partialSum, val) => partialSum + val, 0)
      / 1000
    );

  }, [installationResult]);

  useEffect(() => {
    const loadExistingPv = async () => {
      console.log('Loading existing PV for buildingId:', buildingId);
      if (buildingId) {
        try {
          const existing_pv_data = await getExistingPv(buildingId);
          setExistingPvResult(existing_pv_data);
        }
        catch (error) {
          console.error('Failed to load existing PV:', error);
          posthog.capture('pvpro_error', {
            errorType: 'existing_pv_fetch',
            buildingId: buildingId,
          });
          setExistingPvResult(null);
        }
      }
    };

    loadExistingPv();
  }, [buildingId]);

  useEffect(() => {
    const loadSurfaces = async () => {
      console.log('Loading surfaces for buildingId:', buildingId);
      if (buildingId) {
        try {
          const surfaces_data = await getSurfaces(buildingId);
          setSurfaces(surfaces_data);
        }
        catch (error) {
          console.error('Failed to load surfaces:', error);
          posthog.capture('pvpro_error', {
            errorType: 'surfaces_fetch',
            buildingId: buildingId,
          });
          setSurfaces(null);
        }
      }
    };

    loadSurfaces();
  }, [buildingId]);

  useEffect(() => {
    const loadResults = async () => {
      console.log('Loading results for buildingId:', buildingId);
      if (buildingId && locationState?.parameters) {
        try {
          const result = await getPanelPlacement(buildingId, locationState.parameters, { selected_surfaces: selectedSurfaces });
          setInstallationResult(result);

          if (result.panels.length > 0) {
            const coordinates = result.panels.flatMap(feature =>
              feature.geometry.coordinates[0] // First polygon, first ring
            );
            const lats = coordinates.map(coord => coord[1]);
            const lngs = coordinates.map(coord => coord[0]);
            const min = [Math.min(...lngs), Math.min(...lats)];
            const max = [Math.max(...lngs), Math.max(...lats)];
            // reproject coordinates from utm to wgs
            const min_wgs = proj4(`EPSG:${result.crs}`, 'EPSG:4326', min);
            const max_wgs = proj4(`EPSG:${result.crs}`, 'EPSG:4326', max);

            const bounds = latLngBounds(
              min_wgs.reverse() as LatLngTuple, // reverse to [lat, lng]
              max_wgs.reverse() as LatLngTuple
            );
            setMapBounds(bounds);
          }
        } catch (error) {
          console.error('Failed to load results:', error);
          posthog.capture('pvpro_error', {
            errorType: 'load_panel_placement',
            buildingId: buildingId,
            parameters: locationState.parameters,
          });
          alert('Fehler beim Laden der Modulauslegung. Bitte versuchen Sie es später erneut.');
        }
      }
    };

    loadResults();

  }, [buildingId, locationState?.parameters]);

  if (!locationState?.parameters) {

    return (
      <Navigate to="/" />
    );
  }

  const handleSurfaceClick = (surfaceId: string) => {
    // toggle selection
    setSelectedSurfaces((prevSelectedSurfaces) => {
      if (prevSelectedSurfaces.includes(surfaceId)) {
        return prevSelectedSurfaces.filter(id => id !== surfaceId);
      } else {
        return [...prevSelectedSurfaces, surfaceId];
      }
    });
  }

  const handleSurfaceHover = (surfaceId: string) => {
    setHoveredSurface(surfaceId);
    // set tab to show the hovered surface
    const index = surfaces?.features.findIndex(s => s.properties.surface_gmlid === surfaceId);
    if (index !== undefined && index >= 0) {
      setTabIndex(index);
    }
  };

  const recalculate = () => {
    // Recalculate panel placement with selected surfaces
    setInstallationResult(null); // clear old panels
    const parameters = newParameters;
    parameters.selected_surfaces = selectedSurfaces;
    navigate(`/results/${buildingId}`, {
      state: { parameters, building: buildingId }
    });
  }

  if (!installationResult) {

    return (
      <MapLayout map={(
        <MapLoadingOverlay />
      )}>
        <MapSidebar>
          <MapSidebarHeader />
          <Box padding={3}>
            <Typography variant="body2">
            Berechne Modulauslegung... Dies kann bis zu zwei Minuten dauern - bitte laden Sie die Seite nicht neu. <LoadingEllipsis />
            </Typography>
          </Box>
        </MapSidebar>
      </MapLayout>
    );
  }

  return (
    <MapLayout map={(
      <ResultMap
        buildingId={buildingId}
        panelGeoJSON={panelGeoJSON}
        obstructionGeoJSON={obstructionGeoJSON}
        surfacesGeoJSON={surfacesGeoJSON}
        existingPvGeoJSON={existingPvGeoJSON}
        mapBounds={mapBounds}
        handleSurfaceClick={handleSurfaceClick}
        handleSurfaceHover={handleSurfaceHover}
        selectedSurfaces={selectedSurfaces}
      />
    )}>
      <MapSidebar sx={{ position: 'relative' }}>
        <Fab
          sx={{ position: 'absolute', top: 16, right: 16 }}
          onClick={() => navigate('/')}
        >
          <ArrowLeft />
        </Fab>
        <MapSidebarHeader buildingId={buildingId} />
        {(installationResult.considered_surfaces.length <= 1) ? <></> :
        <Box padding={3}>
          <Typography variant="h4" marginBottom={2}>Modulauslegung</Typography>
          <ParagraphLineWith
            start="Gesamte Dachfläche"
            end={(surfacesGeoJSON?.features.reduce((a,o)=>{return a+o.properties.area;}, 0).toFixed() || '–') + ' m²'}
          />
          <ParagraphLineWith
            start="Anzahl berücksichtigter Dachflächen"
            end={installationResult.considered_surfaces.length}
          />
          <ParagraphLineWith
            start="Anzahl Solarmodule"
            end={totalPanels?.toLocaleString() || '–'}
          />
          <ParagraphLineWith
            start="Gesamte Modulfläche"
            end={(totalArea?.toLocaleString(undefined, {maximumFractionDigits: 2}) || '–') + ' m²'}
            marginBottom={2}
          />
          <ParagraphLineWith
            start="Nennleistung gesamt"
            end={(locationState.parameters.width * locationState.parameters.height * locationState.parameters.efficiency * totalPanels).toLocaleString(undefined, {maximumFractionDigits: 1}) + ' kWp'}
          />
          <ParagraphLineWith
            start="Jährlicher Gesamtertrag"
            end={totalAnnualPower.toLocaleString() + ' kWh/a'}
            marginBottom={2}
          />
          <BarChartComponent data={sumSeries(installationResult.pv_power_series)} />
        </Box>}
        {installationResult.considered_surfaces.length > 0 && (
          <Tabs
            currentTab={tabIndex}
            onTabChange={setTabIndex}
            flexGrow={1}
            sx={{ '& .MuiTabs-scroller': { marginLeft: 1 } }}
          >
            {surfacesGeoJSON?.features.map(x => x.properties.surface_gmlid).map((seriesKey, index) => (
              <TabContent
                key={seriesKey}
                padding={3}
                label={`Dachfläche ${surfacesGeoJSON?.features.map(x => x.properties.surface_gmlid).indexOf(seriesKey) + 1}`}
              >
                <ParagraphLineWith
                  start="Fläche"
                  end={surfacesGeoJSON?.features[index].properties.area.toFixed() + "m²"} 
                />
                <ParagraphLineWith
                  start="Dachneigung"
                  end={surfacesGeoJSON?.features[index].properties.tilt.toFixed() + '°'}
                />
                <ParagraphLineWith
                  start="Ausrichtung"
                  end={azimuthToCardinal(surfacesGeoJSON?.features[index].properties.azimuth, surfacesGeoJSON?.features[index].properties.tilt)}
                />
                {installationResult.panels_per_surface[seriesKey] && 
                <>
                  <ParagraphLineWith
                    start="Anzahl Module"
                    end={installationResult.panels_per_surface[seriesKey].length}
                  />
                  <ParagraphLineWith
                    start="Modulfläche"
                    end={installationResult.panel_area_per_surface[seriesKey].toLocaleString(undefined, {maximumFractionDigits: 2}) + ' m²'}
                    marginBottom={2}
                  />
                  <ParagraphLineWith
                    start="Nennleistung"
                    end={(locationState.parameters.width * locationState.parameters.height * locationState.parameters.efficiency * installationResult.panels_per_surface[seriesKey].length).toLocaleString(undefined, {maximumFractionDigits: 1}) + ' kWp'}
                  />
                  <ParagraphLineWith
                    start="Jährlicher Ertrag"
                    end={<AnnualPower series={installationResult.pv_power_series[seriesKey]} />}
                    marginBottom={2}
                  />
                  <BarChartComponent data={installationResult.pv_power_series[seriesKey]} />
                </>}
              </TabContent>
            ))}
          </Tabs>
        )}
        
        <PanelParameterControls
          padding={3}
          parameters={newParameters}
          onParametersChange={setNewParameters}
        />
        <Button
          onClick={recalculate}>
          Modulauslegung auf ausgewählten Dachflächen neu berechnen
        </Button>
      </MapSidebar>
    </MapLayout>
  );
};

export default ResultsPage;